import { Injectable } from '@angular/core';
import { LatLng, latLngBounds, LatLngBounds, TileLayer, Util } from 'leaflet';

@Injectable({
  providedIn: 'root'
})
export class BingLayerService {
  private _bingLayer: any;

  constructor() {
    this._bingLayer = TileLayer.extend({
      options: {
        subdomains: [0, 1, 2, 3],
        type: 'Aerial',
        attribution: 'Bing',
        culture: '',
      },
      initialize(key: string, options: any): void {
        Util.setOptions(this, options);
        this._key = key;
        this._url = null;
        this._providers = [];
        this.metaRequested = false;
      },

      tile2quad(x, y, z): string {
        let quad = '';
        for (let i = z; i > 0; i--) {
            let digit = 0;
            const mask = 1 << (i - 1); // tslint:disable-line:no-bitwise
            if ((x & mask) !== 0) { digit += 1; } // tslint:disable-line:no-bitwise
            if ((y & mask) !== 0) { digit += 2; } // tslint:disable-line:no-bitwise
            quad = quad + digit;
        }
        return quad;
    },
    getTileUrl(tilePoint): string {
        const zoom = this._getZoomForUrl();
        const subdomains = this.options.subdomains;
        const s = this.options.subdomains[Math.abs((tilePoint.x + tilePoint.y) % subdomains.length)];
        return this._url.replace('{subdomain}', s)
            .replace('{quadkey}', this.tile2quad(tilePoint.x, tilePoint.y, zoom))
            .replace('{culture}', this.options.culture);
    },

    loadMetadata(): void {
        if (this.metaRequested) { return; }
        this.metaRequested = true;
        const _this = this;
        const cbid = '_bing_metadata_' + Util.stamp(this);
        window[cbid] = (meta) => {
            window[cbid] = undefined;
            const e = document.getElementById(cbid);
            e.parentNode.removeChild(e);
            if (meta.errorDetails) {
                throw new Error(meta.errorDetails);
            }
            _this.initMetadata(meta);
        };
        const urlScheme = (document.location.protocol === 'file:') ? 'http' : document.location.protocol.slice(0, -1);
        const url = urlScheme + '://dev.virtualearth.net/REST/v1/Imagery/Metadata/'
            + this.options.type + '?include=ImageryProviders&jsonp=' + cbid +
            '&key=' + this._key + '&UriScheme=' + urlScheme;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = cbid;
        document.getElementsByTagName('head')[0].appendChild(script);
    },

    initMetadata(meta): void {
        const r = meta.resourceSets[0].resources[0];
        this.options.subdomains = r.imageUrlSubdomains;
        this._url = r.imageUrl;
        if (r.imageryProviders) {
            for (const p of r.imageryProviders) {
                for (const c of p.coverageAreas) {
                    const bounds = new LatLngBounds(
                        new LatLng(c.bbox[0] + 0.01, c.bbox[1] + 0.01),
                        new LatLng(c.bbox[2] - 0.01, c.bbox[3] - 0.01),
                    );
                    const coverage = { zoomMin: c.zoomMin, zoomMax: c.zoomMax, active: false, bounds,
                        attrib: p.attribution };
                    this._providers.push(coverage);
                }
            }
        }
        this._update();
    },

    // tslint:disable-next-line:function-name
    _update(): void {
        if (this._url === null || !this._map) { return; }
        this._update_attribution();
        (TileLayer.prototype as any)._update.apply(this, []);
    },

    // tslint:disable-next-line:function-name
    _update_attribution(): void {
        const bounds = latLngBounds(this._map.getBounds().getSouthWest().wrap(),
            this._map.getBounds().getNorthEast().wrap());
        const zoom = this._map.getZoom();
        for (const p of this._providers) {
            if ((zoom <= p.zoomMax && zoom >= p.zoomMin) &&
                bounds.intersects(p.bounds)) {
                if (!p.active && this._map.attributionControl) {
                    this._map.attributionControl.addAttribution(p.attrib);
                }
                p.active = true;
            } else {
                if (p.active && this._map.attributionControl) {
                    this._map.attributionControl.removeAttribution(p.attrib);
                }
                p.active = false;
            }
        }
    },

    onAdd(map): void {
        this.loadMetadata();
        TileLayer.prototype.onAdd.apply(this, [map]);
    },

    onRemove(map): void {
        for (const p of this._providers) {
            if (p.active && this._map.attributionControl) {
                this._map.attributionControl.removeAttribution(p.attrib);
                p.active = false;
            }
        }
        TileLayer.prototype.onRemove.apply(this, [map]);
    },
    });
  }

  bingLayer(key: string, options: any): TileLayer {
    return new this._bingLayer(key, options);
  }
}
